import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * Custom useAuthState Hook for Admin Panel
 * 
 * A safe wrapper around Firebase auth state that prevents useReducer errors
 * by handling the initialization properly and providing fallback states.
 * 
 * This hook ensures:
 * - Proper initialization handling
 * - Safe state management without relying on react-firebase-hooks
 * - Consistent behavior across all admin components
 * 
 * @returns {Array} [user, loading, error] - Authentication state
 */
export function useAuthState() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Safety check to ensure auth is initialized
    if (!auth) {
      setError(new Error('Firebase auth not initialized'));
      setLoading(false);
      return;
    }

    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        setUser(user);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Auth state change error:', error);
        setError(error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return [user, loading, error];
}

export default useAuthState; 